(() => {
  'use strict';
  if (customElements.get('ivori-kids-quick-upgrade')) return;
  class IvoriKidsQuickUpgrade extends HTMLElement {
    connectedCallback() {
      if (this.upgradeReady) return;
      this.page = document.querySelector('ivori-product-page.ivori-kids');
      if (!this.page || (matchMedia('(min-width: 750px)').matches &&
          !this.closest('.ivori-quick-checkout.ivori-add-to-cart-drawer'))) return;
      try { this.data = JSON.parse(this.querySelector('[data-ivori-kids-upgrade-data]').textContent); }
      catch (error) { return; }
      this.catalog = window.IvoriPacks;
      if (!this.catalog || this.catalog.currency !== this.data.currency) return;
      this.variants = new Map(this.data.variants.map(v => [String(v.id),v]));
      this.sources = this.data.sources;
      this.fixedIds = this.sources.flatMap(s => Array(Number(s.quantity)).fill(String(s.variantId)));
      this.selects = [...this.querySelectorAll('[data-ivori-kids-upgrade-select]')];
      this.button = this.querySelector('[data-ivori-kids-upgrade-action]');
      this.error = this.querySelector('[data-ivori-kids-upgrade-error]');
      if (!this.button || !this.error || this.fixedIds.length !== 1 || this.data.units !== 1 ||
          this.selects.length !== 2) return;
      this.upgradeReady = true;
      this.busy = false;
      this.selects.forEach(input => input.addEventListener('change',() => this.preview()));
      this.button.addEventListener('click',() => this.upgrade());
      this.preview();
    }
    money(value) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB',
        {style:'currency',currency:this.data.currency,currencyDisplay:'narrowSymbol'}).format(value/100);
    }
    copy(key,amount) {
      return (this.data.labels[key] || '').replace('__AMOUNT__',this.money(amount || 0));
    }
    message(text) {
      this.error.textContent = text;
      this.error.hidden = !text;
    }
    fingerprint(lines) {
      return JSON.stringify(lines.map(s => [String(s.key),String(s.variantId),Number(s.quantity)])
        .sort((a,b) => a[0].localeCompare(b[0])));
    }
    preview() {
      const chosen = [...this.fixedIds,...this.selects.map(s => String(s.value))];
      const variants = chosen.map(id => this.variants.get(id));
      const packId = variants.length === 3 && variants.every(v => v?.available)
        ? this.catalog.find(chosen) : null;
      const pack = packId && this.catalog.packs[String(packId)];
      this.packId = pack?.available ? String(packId) : null;
      this.button.disabled = this.busy || !this.packId;
      const savingsBadge = this.querySelector('[data-ivori-kids-upgrade-saving]');
      if (savingsBadge) savingsBadge.hidden = !this.packId;
      if (!this.packId) {
        this.button.textContent = this.data.labels.unavailable;
      } else {
        const retail = variants.reduce((sum,v) => sum + Number(v.price || 0),0);
        const savings = Math.max(0,retail - Number(pack.price));
        const previouslyPaid = this.sources.reduce((sum,s) => sum + Number(s.linePrice || 0),0);
        const extra = Math.max(0,Number(pack.price) - previouslyPaid);
        if (savingsBadge) savingsBadge.textContent = this.copy('save',savings);
        this.button.textContent = extra ? this.copy('action',extra) : this.data.labels.freeAction;
      }
      this.hidden = false;
    }
    async getCart() {
      const response = await fetch(this.page.dataset.cartUrl+'.js',{headers:{Accept:'application/json'}});
      if (!response.ok) throw new Error(this.data.labels.error);
      return response.json();
    }
    async redraw(cart) {
      let sections = cart.sections || {};
      if (!sections['ivori-quick-checkout'] || !sections['cart-icon-bubble']) {
        const response = await fetch(this.page.dataset.cartUrl+'?sections=ivori-quick-checkout,cart-icon-bubble');
        if (!response.ok) throw new Error(this.data.labels.error);
        sections = await response.json();
      }
      const parsed = sections['ivori-quick-checkout']
        && new DOMParser().parseFromString(sections['ivori-quick-checkout'],'text/html');
      const incoming = parsed?.querySelector('[data-ivori-checkout-order]');
      const content = this.closest('[data-ivori-checkout-content]');
      if (!incoming?.querySelector('form') || !content) throw new Error(this.data.labels.error);
      const icon = document.getElementById('cart-icon-bubble');
      const markup = sections['cart-icon-bubble']
        && new DOMParser().parseFromString(sections['cart-icon-bubble'],'text/html').querySelector('.shopify-section');
      if (icon && markup) icon.innerHTML = markup.innerHTML;
      content.replaceChildren(incoming);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try { publish(PUB_SUB_EVENTS.cartUpdate,{source:'ivori-kids-pack-upgrade',cartData:cart}); }
        catch (error) {}
      }
      document.dispatchEvent(new CustomEvent('cart:refresh',{detail:{source:'ivori-kids-pack-upgrade'}}));
    }
    async upgrade() {
      if (this.busy || !this.packId) return;
      const content = this.closest('[data-ivori-checkout-content]');
      if (content?.getAttribute('aria-busy') === 'true'
        || content?.querySelector('[data-ivori-checkout-key][aria-busy="true"],ivori-kids-cart-addons[aria-busy="true"]')) {
        this.message(this.data.labels.changed);return;
      }
      this.busy = true;
      this.setAttribute('aria-busy','true');
      this.button.disabled = true;
      this.button.textContent = this.data.labels.loading;
      this.selects.forEach(select => select.disabled = true);
      this.message('');
      const packId = this.packId;
      try {
        const fresh = await this.getCart();
        const actual = (fresh.items || []).filter(line =>
          Number(line.product_id) === Number(this.data.kidsId)
          && Number(line.quantity) > 0 && !line.selling_plan_allocation
          && !Object.keys(line.properties || {}).length)
          .map(line => ({key:line.key,variantId:line.variant_id,quantity:line.quantity}));
        const totalBrushes = (fresh.items || []).filter(line =>
          [Number(this.data.kidsId),Number(this.data.adultId)].includes(Number(line.product_id)))
          .reduce((total,line) => total + Number(line.quantity || 0),0);
        const containsPack = (fresh.items || []).some(line =>
          [Number(this.data.packId),Number(this.data.adultPackId)].includes(Number(line.product_id)));
        if (this.fingerprint(actual) !== this.fingerprint(this.sources)
          || totalBrushes !== 1 || containsPack) {
          throw new Error(this.data.labels.changed);
        }
        const updates = Object.fromEntries(this.sources.map(line => [line.key,0]));
        updates[packId] = 1;
        const response = await fetch(this.page.dataset.cartUrl+'/update.js',{
          method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},
          body:JSON.stringify({updates,sections:['ivori-quick-checkout','cart-icon-bubble'],sections_url:this.page.dataset.cartUrl})
        });
        const updated = await response.json();
        if (!response.ok) throw new Error(updated.description || updated.message || this.data.labels.error);
        if (!updated.items?.some(line => String(line.variant_id || line.id) === packId)) {
          throw new Error(this.data.labels.changed);
        }
        await this.redraw(updated);
      } catch (error) {
        this.message(error.message || this.data.labels.error);
        try { await this.redraw(await this.getCart()); } catch (ignored) {}
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        if (this.isConnected) {
          this.selects.forEach(select => select.disabled = false);
          this.preview();
        }
      }
    }
  }
  customElements.define('ivori-kids-quick-upgrade',IvoriKidsQuickUpgrade);

  class IvoriKidsCartAddons extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.page = document.querySelector('ivori-product-page.ivori-kids');
      if (!this.page || (matchMedia('(min-width: 750px)').matches &&
          !this.closest('.ivori-quick-checkout.ivori-add-to-cart-drawer'))) return;
      this.content = this.closest('[data-ivori-checkout-content]');
      this.cards = [...this.querySelectorAll('[data-ivori-kids-addon]')];
      this.status = this.querySelector('[data-ivori-kids-addon-status]');
      if (!this.content || !this.cards.length) return;
      this.initialized = true;
      this.busy = false;
      this.cards.forEach(card => {
        const button = card.querySelector('[data-ivori-kids-addon-button]');
        button?.addEventListener('click', () => this.addToCart(card));
      });
      this.hidden = false;
    }
    showStatus(message) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
    }
    async getRenderedCart(sections) {
      let html = sections || {};
      if (!html['ivori-quick-checkout'] || !html['cart-icon-bubble']) {
        const url = this.page.dataset.cartUrl + '?sections=ivori-quick-checkout,cart-icon-bubble';
        const response = await fetch(url,{headers:{Accept:'application/json'}});
        if (!response.ok) throw new Error('Cart refresh unavailable');
        html = await response.json();
      }
      const documentFragment = html['ivori-quick-checkout']
        && new DOMParser().parseFromString(html['ivori-quick-checkout'],'text/html');
      const order = documentFragment?.querySelector('[data-ivori-checkout-order]');
      if (!order?.querySelector('form')) throw new Error('Cart refresh unavailable');
      const icon = document.getElementById('cart-icon-bubble');
      const iconContent = html['cart-icon-bubble'] && new DOMParser()
        .parseFromString(html['cart-icon-bubble'],'text/html').querySelector('.shopify-section');
      if (icon && iconContent) icon.innerHTML = iconContent.innerHTML;
      return order;
    }
    async addToCart(card) {
      if (this.busy || this.content?.getAttribute('aria-busy') === 'true' ||
          this.content?.querySelector('ivori-kids-quick-upgrade[aria-busy="true"],[data-ivori-checkout-key][aria-busy="true"]')) return;
      const button = card.querySelector('[data-ivori-kids-addon-button]');
      const select = card.querySelector('[data-ivori-kids-addon-select]');
      const id = Number(select?.value || card.dataset.ivoriKidsAddonVariantId);
      if (!id || !button || button.disabled || select?.selectedOptions[0]?.disabled) return;
      this.busy = true;
      this.setAttribute('aria-busy','true');
      button.disabled = true;
      if (select) select.disabled = true;
      this.showStatus('');
      let added = false;
      try {
        const response = await fetch(this.page.dataset.cartAddUrl,{
          method:'POST',
          headers:{'Content-Type':'application/json',Accept:'application/json'},
          body:JSON.stringify({
            items:[{id,quantity:1}],
            sections:['ivori-quick-checkout','cart-icon-bubble'],
            sections_url:this.page.dataset.cartUrl
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Could not add this item');
        added = true;
        const order = await this.getRenderedCart(result.sections);
        const content = this.content;
        content.replaceChildren(order);
        document.dispatchEvent(new CustomEvent('cart:refresh',{detail:{source:'ivori-kids-accessory-addon'}}));
      } catch (error) {
        if (this.isConnected) {
          this.showStatus(added ? 'Added to cart. Reopen your cart to see the updated items.' :
            (error.message || 'Could not add this item. Please try again.'));
        }
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        if (this.isConnected) {
          button.disabled = added;
          if (select) select.disabled = false;
        }
      }
    }
  }
  customElements.define('ivori-kids-cart-addons',IvoriKidsCartAddons);
})();
